import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { Loader2, Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { VACATION_ELIGIBLE_ROLES } from "../lib/roles";

/**
 * Iter46 — Dialog za bulk izdavanje kolektivnog godišnjeg odmora.
 * Iter46c — Live progress bar (background job + polling /jobs/{id}).
 */
export function CollectiveIssueDialog({ open, onClose, onIssued }) {
  const [users, setUsers] = useState([]);
  const [info, setInfo] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [allMode, setAllMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);   // live job snapshot iz polling-a
  const [result, setResult] = useState(null); // finalni job.result + summary
  const pollRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/users").then(r => setUsers(r.data.filter(u =>
        VACATION_ELIGIBLE_ROLES.includes(u.role)
      )));
      api.get("/vacation/info").then(r => setInfo(r.data));
      // Defer resets to escape react-hooks/set-state-in-effect strict rule.
      queueMicrotask(() => { setResult(null); setJob(null); });
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.full_name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.katedra || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const canIssue = !busy && (allMode || selectedCount > 0);

  const toggleAll = (checked) => {
    if (checked) {
      const next = {};
      filteredUsers.forEach(u => { next[u.email] = true; });
      setSelected(next);
    } else setSelected({});
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/jobs/${jobId}`);
        setJob(r.data);
        if (r.data.status === "completed") {
          stopPolling();
          setBusy(false);
          setResult(r.data.result || null);
          toast.success(r.data.result?.summary || "Posao završen");
          if (onIssued) onIssued();
        } else if (r.data.status === "failed") {
          stopPolling();
          setBusy(false);
          toast.error(r.data.error || "Posao nije uspio");
        }
      } catch (e) {
        stopPolling();
        setBusy(false);
        toast.error(e.response?.data?.detail || "Greška pri praćenju posla");
      }
    }, 1200);
  };

  const issue = async (autoSend = false) => {
    setBusy(true);
    setResult(null);
    setJob(null);
    try {
      const base = allMode
        ? { all_employees: true }
        : { employee_emails: Object.keys(selected).filter(k => selected[k]) };
      const payload = { ...base, auto_generate_and_send: autoSend };
      const r = await api.post("/vacation/requests/issue-collective-bulk", payload);
      if (r.data?.job_id) {
        startPolling(r.data.job_id);
      } else {
        // Backwards compat — sync response
        setResult(r.data);
        setBusy(false);
        toast.success(r.data.summary);
        if (onIssued) onIssued();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
      setBusy(false);
    }
  };

  const formatRange = () => {
    if (!info) return "";
    const start = info.collective_start?.split("-").reverse().join(".");
    const end = info.collective_end?.split("-").reverse().join(".");
    return `${start} – ${end} (${info.collective_workdays} radnih dana)`;
  };

  const pct = job && job.total_steps
    ? Math.min(100, Math.round((job.processed_steps / job.total_steps) * 100))
    : 0;
  const isRunning = busy && job && job.status === "running";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !isRunning) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5"/>Izdaj rješenja kolektivnog godišnjeg odmora
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-accent/10 border border-accent/30 rounded p-3 text-sm">
            <div className="font-medium">Razdoblje kolektivnog godišnjeg odmora:</div>
            <div className="serif text-lg mt-1">{formatRange()}</div>
            <p className="text-xs text-muted-foreground mt-2">
              Rješenja se kreiraju sa automatskom suglasnošću tajništva (bez zahtjeva zaposlenika).
              Postojeća rješenja za isto razdoblje se preskaču.
            </p>
          </div>

          {/* Live progress (Iter46c) */}
          {(busy || job) && (
            <div className="border rounded p-3 space-y-2 bg-secondary/20" data-testid="collective-progress">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  {job?.status === "completed" ? "Završeno" :
                   job?.status === "failed" ? "Greška" :
                   "U tijeku…"}
                </span>
                <span className="text-xs text-muted-foreground" data-testid="collective-progress-pct">
                  {pct}% {job?.total_steps ? `(${job.processed_steps}/${job.total_steps})` : ""}
                </span>
              </div>
              <Progress value={pct} className="h-2" />
              <div className="text-xs text-muted-foreground" data-testid="collective-progress-step">
                {job?.current_step || "Pokretanje posla…"}
              </div>
              {job?.processed > 0 && job?.total > 0 && (
                <div className="text-[11px] text-muted-foreground">
                  Zaposlenici obrađeni: {job.processed}/{job.total}
                </div>
              )}
            </div>
          )}

          {!busy && !result && (
            <>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={allMode}
              onCheckedChange={(v) => { setAllMode(v === true); if (v) setSelected({}); }}
              data-testid="collective-all-employees"
              className="mt-0.5"
            />
            <div>
              <div className="font-medium">Izdaj za sve zaposlenike</div>
              <div className="text-xs text-muted-foreground">Uključuje sve zaposlenike, nastavnike, voditelje, prodekane, tajništvo i dekanat ({users.length})</div>
            </div>
          </label>

          {!allMode && (
            <>
              <div className="border-t pt-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm">Ručno odabrani zaposlenici ({selectedCount})</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => toggleAll(true)} data-testid="collective-select-filtered">Odaberi prikazane</Button>
                    <Button variant="outline" size="sm" onClick={() => setSelected({})}>Odznači sve</Button>
                  </div>
                </div>
                <Input
                  placeholder="Pretraga (ime, email, katedra)..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="border rounded max-h-72 overflow-y-auto">
                  {filteredUsers.map(u => (
                    <label
                      key={u.email}
                      className="flex items-center gap-3 px-3 py-1.5 hover:bg-secondary/50 cursor-pointer border-b text-sm"
                    >
                      <Checkbox
                        checked={!!selected[u.email]}
                        onCheckedChange={(v) => setSelected(s => ({...s, [u.email]: v === true}))}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{u.full_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{u.email} · {u.katedra || "—"}</div>
                      </div>
                    </label>
                  ))}
                  {filteredUsers.length === 0 && (
                    <p className="text-sm text-muted-foreground p-3">Nema rezultata pretrage</p>
                  )}
                </div>
              </div>
            </>
          )}
            </>
          )}

          {result && (
            <div className="space-y-2 bg-secondary/30 rounded p-3 text-xs" data-testid="collective-result">
              <div className="font-medium text-sm">{result.summary}</div>
              {result.created?.length > 0 && (
                <div className="text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1"/>
                  Izdano: {result.created.map(c => c.name).slice(0, 8).join(", ")}
                  {result.created.length > 8 && ` … i još ${result.created.length - 8}`}
                </div>
              )}
              {result.pdf_generated !== undefined && result.pdf_generated !== null && (
                <div className="text-blue-700 dark:text-blue-400">
                  PDF generirano: <b>{result.pdf_generated}</b> · Poslano: <b>{result.sent}</b>
                  {result.auto_errors?.length > 0 && <span className="text-destructive ml-1">· Greške u slanju: {result.auto_errors.length}</span>}
                </div>
              )}
              {result.skipped?.length > 0 && (
                <div className="text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1"/>
                  Preskočeno (već postoji): {result.skipped.length}
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="text-destructive">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1"/>
                  Greške: {result.errors.length}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={isRunning} data-testid="collective-close-btn">
            {result ? "Zatvori" : isRunning ? "Pričekajte…" : "Odustani"}
          </Button>
          {!result && (
            <>
              <Button variant="outline" onClick={() => issue(false)} disabled={!canIssue} data-testid="collective-issue-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Users className="w-4 h-4 mr-2"/>}
                Samo izdaj {allMode ? `(${users.length})` : `(${selectedCount})`}
              </Button>
              <Button onClick={() => issue(true)} disabled={!canIssue} data-testid="collective-issue-send-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <CheckCircle2 className="w-4 h-4 mr-2"/>}
                Izdaj + Generiraj + Pošalji
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
